import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, Row, Col, InputNumber, DatePicker, message, Spin, Upload, Modal, Progress } from 'antd';
import { UploadOutlined, DownloadOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { useMutation } from '@apollo/client';
import { gql } from '@apollo/client';

// GraphQL Mutations
const CREATE_ORDER = gql`
  mutation CreateOrder($input: CreateOrderInput!) {
    createOrder(input: $input) {
      id
      trackingNumber
      overallStatus
      createdAt
    }
  }
`;

const BATCH_CREATE_ORDERS = gql`
  mutation BatchCreateOrders($inputs: [CreateOrderInput!]!) {
    batchCreateOrders(inputs: $inputs) {
      successOrders {
        id
        trackingNumber
        overallStatus
        createdAt
      }
      failedRecords {
        orderData {
          senderAddress {
            fullName
            phoneNumber
            addressLine1
            state
            city
            zipcode
          }
          recipientAddress {
            fullName
            phoneNumber
            addressLine1
            state
            city
            zipcode
          }
          channelId
          channelOrderNumber
          expectedDeliveryTime
          volumetricWeightKg
          lengthCm
          widthCm
          heightCm
          packageValueUsd
        }
        errorMessage
        index
      }
      successCount
      failedCount
      totalCount
    }
  }
`;

interface OrderAddress {
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  zipcode: string;
  fullName: string;
  phoneNumber: string;
  email?: string;
  company?: string;
  instructions?: string;
  countryCode?: number;
}

interface CreateOrderFormData {
  senderAddress: OrderAddress;
  recipientAddress: OrderAddress;
  trackingNumber?: string;
  channelId?: number;
  channelOrderNumber?: string;
  expectedDeliveryTime?: any; // Ant Design DatePicker value
  weightKg?: number;
  volumetricWeightKg?: number;
  lengthCm?: number;
  widthCm?: number;
  heightCm?: number;
  packageValueUsd?: number;
  sortingCode?: string;
}

// CSV数据接口
interface CSVOrderData {
  寄件人: string;
  寄件人电话: string;
  寄件人地址: string;
  寄件人州: string;
  寄件人城市: string;
  寄件人ZIPCODE: string;
  收件人: string;
  收件人电话: string;
  收件人地址: string;
  收件人州: string;
  收件人城市: string;
  收件人ZIPCODE: string;
  渠道ID: string;
  渠道订单ID: string;
  预计交付时间: string;
  '体积重量(公斤)': string;
  '长度(厘米)': string;
  '宽度(厘米)': string;
  '高度(厘米)': string;
  '包裹价值(美元)': string;
  运单号: string;
}

const PageBody = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const [createOrder] = useMutation(CREATE_ORDER, {
    onCompleted: (data) => {
      message.success(`订单创建成功！追踪号：${data.createOrder.trackingNumber}`);
      form.resetFields();
      setLoading(false);
    },
    onError: (error) => {
      message.error(`创建订单失败：${error.message}`);
      setLoading(false);
    }
  });

  const [batchCreateOrders] = useMutation(BATCH_CREATE_ORDERS, {
    onCompleted: (data) => {
      const { successCount, failedCount, totalCount, failedRecords } = data.batchCreateOrders;
      
      if (failedCount === 0) {
        message.success(`批量导入成功！共创建了 ${successCount} 个订单`);
      } else if (successCount === 0) {
        message.error(`批量导入失败！所有 ${totalCount} 个订单都导入失败`);
      } else {
        message.warning(`批量导入部分成功！成功 ${successCount} 个，失败 ${failedCount} 个`);
      }

      // 如果有失败记录，导出失败的CSV
      if (failedRecords.length > 0) {
        exportFailedRecords(failedRecords);
      }

      setUploading(false);
      setUploadProgress(0);
      setUploadModalVisible(false);
      setFileList([]);
    },
    onError: (error) => {
      message.error(`批量导入失败：${error.message}`);
      setUploading(false);
      setUploadProgress(0);
    }
  });

  useEffect(() => {
    form.setFieldsValue({
      senderAddress: {
        countryCode: 1,
      },
      recipientAddress: {
        countryCode: 1,
      },
      channelId: 10,
    });
  }, []);   

  const handleSubmit = async (values: CreateOrderFormData) => {
    setLoading(true);
    
    try {
      const input = {
        senderAddress: values.senderAddress,
        recipientAddress: values.recipientAddress,
        channelId: values.channelId,
        trackingNumber: values.trackingNumber,
        channelOrderNumber: values.channelOrderNumber,
        expectedDeliveryTime: values.expectedDeliveryTime ? values.expectedDeliveryTime.toISOString() : undefined,
        weightKg: values.weightKg,
        volumetricWeightKg: values.volumetricWeightKg,
        lengthCm: values.lengthCm,
        widthCm: values.widthCm,
        heightCm: values.heightCm,
        packageValueUsd: values.packageValueUsd,
        sortingCode: values.sortingCode,
      };
      
      await createOrder({ variables: { input } });
    } catch (error) {
      console.error('创建订单失败:', error);
      setLoading(false);
    }
  };

  // 解析CSV文本为对象数组
  const parseCSV = (text: string): CSVOrderData[] => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) {
      throw new Error('CSV文件格式不正确，至少需要包含标题行和一行数据');
    }

    // 改进的CSV解析，支持包含逗号的字段（用引号包围）
    const parseCSVLine = (line: string): string[] => {
      const result: string[] = [];
      let current = '';
      let inQuotes = false;
      
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          result.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      
      result.push(current.trim());
      return result;
    };

    const headers = parseCSVLine(lines[0]);
    const expectedHeaders = [
      '寄件人', '寄件人电话', '寄件人地址', '寄件人州', '寄件人城市', '寄件人ZIPCODE',
      '收件人', '收件人电话', '收件人地址', '收件人州', '收件人城市', '收件人ZIPCODE',
      '渠道ID', '渠道订单ID', '预计交付时间', '体积重量(公斤)', '长度(厘米)', '宽度(厘米)', '高度(厘米)', '包裹价值(美元)',
      '运单号'
    ];

    // 验证标题行
    for (const expectedHeader of expectedHeaders) {
      if (!headers.includes(expectedHeader)) {
        throw new Error(`CSV文件缺少必需的列：${expectedHeader}`);
      }
    }

    const data: CSVOrderData[] = [];
    
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue; // 跳过空行
      
      const values = parseCSVLine(line);
      if (values.length !== headers.length) {
        console.warn(`第 ${i + 1} 行数据列数不匹配，跳过该行。期望 ${headers.length} 列，实际 ${values.length} 列`);
        continue;
      }

      const rowData: any = {};
      headers.forEach((header, index) => {
        rowData[header] = values[index];
      });
      
      data.push(rowData as CSVOrderData);
    }

    if (data.length === 0) {
      throw new Error('CSV文件中没有有效的数据行');
    }

    return data;
  };

  // 将CSV数据转换为GraphQL输入格式
  const convertCSVToOrderInputs = (csvData: CSVOrderData[]) => {
    return csvData.map((row) => ({
      senderAddress: {
        fullName: row.寄件人,
        phoneNumber: row.寄件人电话,
        addressLine1: row.寄件人地址,
        city: row.寄件人城市,
        state: row.寄件人州,
        zipcode: row.寄件人ZIPCODE,
        countryCode: 1,
      },
      recipientAddress: {
        fullName: row.收件人,
        phoneNumber: row.收件人电话,
        addressLine1: row.收件人地址,
        city: row.收件人城市,
        state: row.收件人州,
        zipcode: row.收件人ZIPCODE,
        countryCode: 1,
      },
      channelId: row.渠道ID ? parseInt(row.渠道ID) : null,
      channelOrderNumber: row.渠道订单ID || null,
      expectedDeliveryTime: row.预计交付时间 || null,
      volumetricWeightKg: row['体积重量(公斤)'] ? parseFloat(row['体积重量(公斤)']) : null,
      lengthCm: row['长度(厘米)'] ? parseFloat(row['长度(厘米)']) : null,
      widthCm: row['宽度(厘米)'] ? parseFloat(row['宽度(厘米)']) : null,
      heightCm: row['高度(厘米)'] ? parseFloat(row['高度(厘米)']) : null,
      packageValueUsd: row['包裹价值(美元)'] ? parseFloat(row['包裹价值(美元)']) : null,
      trackingNumber: row.运单号 || null,
    }));
  };

  // 导出失败记录为CSV
  const exportFailedRecords = (failedRecords: any[]) => {
    const headers = [
      '错误信息',
      '寄件人', '寄件人电话', '寄件人地址', '寄件人州', '寄件人城市', '寄件人ZIPCODE',
      '收件人', '收件人电话', '收件人地址', '收件人州', '收件人城市', '收件人ZIPCODE',
      '渠道ID', '渠道订单ID', '预计交付时间', '体积重量(公斤)', '长度(厘米)', '宽度(厘米)', '高度(厘米)', '包裹价值(美元)', 
      '运单号'
    ];

    const csvContent = [
      headers.join(','),
      ...failedRecords.map(record => {
        const order = record.orderData;
        return [
          `"${record.errorMessage}"`,
          order.senderAddress.fullName,
          order.senderAddress.phoneNumber,
          order.senderAddress.addressLine1,
          order.senderAddress.state,
          order.senderAddress.city,
          order.senderAddress.zipcode,
          order.recipientAddress.fullName,
          order.recipientAddress.phoneNumber,
          order.recipientAddress.addressLine1,
          order.recipientAddress.state,
          order.recipientAddress.city,
          order.recipientAddress.zipcode,
          order.channelId || '',
          order.channelOrderNumber || '',
          order.expectedDeliveryTime || '',
          order.volumetricWeightKg || '',
          order.lengthCm || '',
          order.widthCm || '',
          order.heightCm || '',
          order.packageValueUsd || '',
          order.trackingNumber || '',
        ].join(',');
      })
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `failed_orders_${new Date().getTime()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    message.info('失败记录已导出为CSV文件，请检查并修正后重新导入');
  };

  // 处理文件上传
  const handleFileUpload = async () => {
    if (fileList.length === 0) {
      message.error('请选择CSV文件');
      return;
    }

    const file = fileList[0];
    console.log('Selected file:', file);
    
    // 获取实际的File对象
    let actualFile: File;
    if (file.originFileObj) {
      actualFile = file.originFileObj;
    } else if (file instanceof File) {
      actualFile = file;
    } else {
      console.error('Cannot find actual file object:', file);
      message.error('文件对象获取失败，请重新选择文件');
      return;
    }

    console.log('Actual file details:', {
      name: actualFile.name,
      size: actualFile.size,
      type: actualFile.type,
      lastModified: actualFile.lastModified
    });

    try {
      setUploading(true);
      setUploadProgress(10);

      // 读取文件内容
      const text = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const result = e.target?.result;
          console.log('File read result type:', typeof result, 'length:', result?.toString().length);
          
          if (typeof result === 'string' && result.length > 0) {
            resolve(result);
          } else {
            reject(new Error('文件内容为空或读取失败'));
          }
        };
        reader.onerror = (error) => {
          console.error('FileReader error:', error);
          reject(new Error('文件读取过程中发生错误'));
        };
        reader.onabort = () => {
          reject(new Error('文件读取被中断'));
        };
        
        try {
          console.log('Starting to read file...');
          reader.readAsText(actualFile, 'utf-8');
        } catch (error) {
          console.error('ReadAsText error:', error);
          reject(new Error('无法启动文件读取'));
        }
      });

      console.log('File content preview:', text.substring(0, 200));
      setUploadProgress(30);

      // 解析CSV
      const csvData = parseCSV(text);
      console.log('解析的CSV数据:', csvData);

      setUploadProgress(50);

      // 转换为GraphQL输入格式
      const orderInputs = convertCSVToOrderInputs(csvData);
      console.log('转换后的订单输入:', orderInputs);

      setUploadProgress(70);

      // 调用批量创建API
      await batchCreateOrders({ variables: { inputs: orderInputs } });

      setUploadProgress(100);
    } catch (error: any) {
      console.error('文件处理失败:', error);
      message.error(`文件处理失败：${error.message}`);
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const uploadProps = {
    beforeUpload: (file: any) => {
      console.log('beforeUpload called with file:', file);
      console.log('File properties:', {
        name: file.name,
        type: file.type,
        size: file.size,
        lastModified: file.lastModified
      });
      
      // 检查文件扩展名和MIME类型
      const isCSV = file.type === 'text/csv' || 
                   file.type === 'application/csv' || 
                   file.type === 'text/plain' ||
                   file.name.toLowerCase().endsWith('.csv');
      
      if (!isCSV) {
        message.error('只能上传CSV文件（.csv）');
        return false;
      }
      
      // 检查文件大小（10MB限制）
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error('文件大小不能超过10MB');
        return false;
      }
      
      console.log('File validation passed, adding to fileList');
      
      // 创建UploadFile对象，确保originFileObj正确设置
      const uploadFile = {
        uid: file.uid || Date.now().toString(),
        name: file.name,
        status: 'done' as const,
        size: file.size,
        type: file.type,
        originFileObj: file,
        lastModified: file.lastModified
      };
      
      console.log('Created uploadFile:', uploadFile);
      setFileList([uploadFile]);
      return false; // 阻止自动上传
    },
    fileList,
    onRemove: () => {
      console.log('File removed');
      setFileList([]);
    },
    accept: '.csv,text/csv,application/csv',
    maxCount: 1,
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">创建测试订单</h1>
            <p className="text-gray-600 mt-2">填写订单信息以创建新的测试快递订单</p>
            <p className="text-gray-600 mt-2">注意：测试订单不会实际发货，仅用于测试系统功能</p>
          </div>
          <Button 
            type="default" 
            icon={<UploadOutlined />}
            size="large"
            onClick={() => setUploadModalVisible(true)}
          >
            批量导入订单
          </Button>
        </div>
      </div>

      {/* 批量导入Modal */}
      <Modal
        title="批量导入测试订单"
        open={uploadModalVisible}
        onCancel={() => {
          if (!uploading) {
            setUploadModalVisible(false);
            setFileList([]);
            setUploadProgress(0);
          }
        }}
        footer={[
          <Button 
            key="cancel" 
            onClick={() => {
              if (!uploading) {
                setUploadModalVisible(false);
                setFileList([]);
                setUploadProgress(0);
              }
            }}
            disabled={uploading}
          >
            取消
          </Button>,
          <Button 
            key="upload" 
            type="primary" 
            onClick={handleFileUpload}
            loading={uploading}
            disabled={fileList.length === 0}
          >
            开始导入
          </Button>
        ]}
        width={600}
        closable={!uploading}
        maskClosable={!uploading}
      >
        <div className="space-y-4">
          <div>
            <h4 className="font-medium mb-2">CSV文件格式要求：</h4>
            <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
              <p>CSV文件需包含以下列（按顺序）：</p>
              <div className="mt-2 font-mono text-xs">
                寄件人,寄件人电话,寄件人地址,寄件人州,寄件人城市,寄件人ZIPCODE,<br/>
                收件人,收件人电话,收件人地址,收件人州,收件人城市,收件人ZIPCODE,<br/>
                渠道ID,渠道订单ID,预计交付时间,体积重量(公斤),长度(厘米),宽度(厘米),高度(厘米),包裹价值(美元),运单号
              </div>
              <div className="mt-2">
                <a 
                  href="/batch_order_template.csv" 
                  download="批量订单模板.csv"
                  className="text-blue-500 hover:text-blue-700 text-sm"
                >
                  <DownloadOutlined className="mr-1" />
                  下载CSV模板文件
                </a>
              </div>
            </div>
          </div>
          
          <div>
            <h4 className="font-medium mb-2">如何准备CSV文件：</h4>
            <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded">
              <div className="space-y-1">
                <p><strong>使用Excel：</strong></p>
                <p>1. 创建或编辑Excel文件</p>
                <p>2. 文件 → 另存为 → 选择"CSV UTF-8（逗号分隔）"格式</p>
                <p>3. 确保所有必需字段都已填写</p>
                <p className="mt-2"><strong>常见问题：</strong></p>
                <p>• 如果地址包含逗号，请用双引号包围整个地址</p>
                <p>• 确保文件使用UTF-8编码保存</p>
                <p>• 避免在数据中使用换行符</p>
              </div>
            </div>
          </div>
          
          <div>
            <h4 className="font-medium mb-2">选择CSV文件：</h4>
            <Upload {...uploadProps}>
              <Button icon={<UploadOutlined />}>选择文件</Button>
            </Upload>
            {fileList.length > 0 && (
              <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-sm">
                <p className="text-green-700">
                  <span className="font-medium">已选择文件：</span>{fileList[0].name}
                </p>
                <p className="text-green-600 text-xs">
                  大小：{(fileList[0].size! / 1024).toFixed(1)} KB
                </p>
              </div>
            )}
          </div>
          
          {uploading && (
            <div>
              <h4 className="font-medium mb-2">导入进度：</h4>
              <Progress percent={uploadProgress} status="active" />
            </div>
          )}
          
          <div className="text-sm text-gray-500">
            <p>• 支持的文件格式：CSV (.csv)</p>
            <p>• 文件编码：UTF-8（推荐使用Excel另存为CSV UTF-8格式）</p>
            <p>• 最大文件大小：10MB</p>
            <p>• 失败的记录将自动导出为CSV文件，可修正后重新导入</p>
            <p>• 预计交付时间格式：YYYY-MM-DDTHH:mm:ssZ（可选字段）</p>
          </div>
        </div>
      </Modal>

      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          className="space-y-6"
        >
          {/* 发件人地址 */}
          <Card title="发件人信息" className="shadow-sm">
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name={['senderAddress', 'fullName']}
                  label="姓名"
                  rules={[{ required: true, message: '请输入发件人姓名' }]}
                >
                  <Input placeholder="请输入发件人姓名" />
                </Form.Item>
              </Col>
              <Col span={16} className='flex gap-2'>
                <Form.Item
                  name={['senderAddress', 'countryCode']}
                  label="国家代码"
                >
                  <InputNumber placeholder="请输入国家代码" className="w-full"/>
                </Form.Item>
                <Form.Item
                  name={['senderAddress', 'phoneNumber']}
                  label="电话号码"
                  rules={[{ required: true, message: '请输入电话号码' }]}
                >
                  <Input placeholder="请输入电话号码" />
                </Form.Item>
              </Col>
            </Row>
            
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name={['senderAddress', 'addressLine1']}
                  label="地址行1"
                  rules={[{ required: true, message: '请输入街道地址' }]}
                >
                  <Input placeholder="请输入街道地址" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name={['senderAddress', 'addressLine2']}
                  label="地址行2"
                >
                  <Input placeholder="公寓号、单元号等（可选）" />
                </Form.Item>
              </Col>
            </Row>
            
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name={['senderAddress', 'city']}
                  label="城市"
                  rules={[{ required: true, message: '请输入城市' }]}
                >
                  <Input placeholder="请输入城市" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={['senderAddress', 'state']}
                  label="州"
                  rules={[{ required: true, message: '请输入州' }]}
                >
                  <Input placeholder="请输入州（两字母缩写）" maxLength={2} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={['senderAddress', 'zipcode']}
                  label="邮编"
                  rules={[{ required: true, message: '请输入邮编' }]}
                >
                  <Input placeholder="请输入邮编" />
                </Form.Item>
              </Col>
            </Row>
            
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name={['senderAddress', 'email']}
                  label="邮箱"
                  rules={[{ type: 'email', message: '请输入有效的邮箱地址' }]}
                >
                  <Input placeholder="请输入邮箱（可选）" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name={['senderAddress', 'company']}
                  label="公司名称"
                >
                  <Input placeholder="请输入公司名称（可选）" />
                </Form.Item>
              </Col>
            </Row>
            
            <Form.Item
              name={['senderAddress', 'instructions']}
              label="配送说明"
            >
              <Input.TextArea placeholder="请输入配送说明（可选）" rows={2} />
            </Form.Item>
          </Card>

          {/* 收件人地址 */}
          <Card title="收件人信息" className="shadow-sm">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name={['recipientAddress', 'fullName']}
                  label="姓名"
                  rules={[{ required: true, message: '请输入收件人姓名' }]}
                >
                  <Input placeholder="请输入收件人姓名" />
                </Form.Item>
              </Col>
              <Col span={12} className='flex gap-2'>
                <Form.Item
                  name={['recipientAddress', 'countryCode']}
                  label="国家代码"
                >
                  <InputNumber placeholder="请输入国家代码" className="w-full"/>
                </Form.Item>
                <Form.Item
                  name={['recipientAddress', 'phoneNumber']}
                  label="电话号码"
                  rules={[{ required: true, message: '请输入电话号码' }]}
                >
                  <Input placeholder="请输入电话号码" />
                </Form.Item>
              </Col>
            </Row>
            
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name={['recipientAddress', 'addressLine1']}
                  label="地址行1"
                  rules={[{ required: true, message: '请输入街道地址' }]}
                >
                  <Input placeholder="请输入街道地址" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name={['recipientAddress', 'addressLine2']}
                  label="地址行2"
                >
                  <Input placeholder="公寓号、单元号等（可选）" />
                </Form.Item>
              </Col>
            </Row>
            
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name={['recipientAddress', 'city']}
                  label="城市"
                  rules={[{ required: true, message: '请输入城市' }]}
                >
                  <Input placeholder="请输入城市" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={['recipientAddress', 'state']}
                  label="州"
                  rules={[{ required: true, message: '请输入州' }]}
                >
                  <Input placeholder="请输入州（两字母缩写）" maxLength={2} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={['recipientAddress', 'zipcode']}
                  label="邮编"
                  rules={[{ required: true, message: '请输入邮编' }]}
                >
                  <Input placeholder="请输入邮编" />
                </Form.Item>
              </Col>
            </Row>
            
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name={['recipientAddress', 'email']}
                  label="邮箱"
                  rules={[{ type: 'email', message: '请输入有效的邮箱地址' }]}
                >
                  <Input placeholder="请输入邮箱（可选）" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name={['recipientAddress', 'company']}
                  label="公司名称"
                >
                  <Input placeholder="请输入公司名称（可选）" />
                </Form.Item>
              </Col>
            </Row>
            
            <Form.Item
              name={['recipientAddress', 'instructions']}
              label="配送说明"
            >
              <Input.TextArea placeholder="请输入配送说明（可选）" rows={2} />
            </Form.Item>
          </Card>

          {/* 订单信息 */}
          <Card title="订单信息" className="shadow-sm">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="trackingNumber"
                  label="运单号"
                >
                  <Input placeholder="为空则系统自动生成标准运单号" className="w-full" />
                </Form.Item>
              </Col>
              
            </Row>
            
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="channelId"
                  label="渠道ID"
                >
                  <InputNumber placeholder="请输入渠道ID（可选）" className="w-full" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="channelOrderNumber"
                  label="渠道订单ID"
                >
                  <Input placeholder="请输入渠道订单ID（可选）" />
                </Form.Item>
              </Col>
            </Row>
            
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="expectedDeliveryTime"
                  label="预计交付时间"
                >
                  <DatePicker 
                    showTime 
                    placeholder="请选择预计交付时间（可选）" 
                    className="w-full"
                    format="YYYY-MM-DD HH:mm:ss"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="packageValueUsd"
                  label="包裹价值(美元)"
                >
                  <InputNumber 
                    placeholder="请输入包裹价值" 
                    className="w-full"
                    min={0}
                    precision={2}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* 包裹尺寸和重量 */}
          <Card title="包裹尺寸和重量" className="shadow-sm">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="weightKg"
                  label="重量(公斤)"
                >
                  <InputNumber 
                    placeholder="请输入重量" 
                    className="w-full"
                    min={0}
                    precision={3}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="volumetricWeightKg"
                  label="体积重量(公斤)"
                >
                  <InputNumber 
                    placeholder="请输入体积重量" 
                    className="w-full"
                    min={0}
                    precision={3}
                  />
                </Form.Item>
              </Col>
            </Row>
            
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="lengthCm"
                  label="长度(厘米)"
                >
                  <InputNumber 
                    placeholder="请输入长度" 
                    className="w-full"
                    min={0}
                    precision={2}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="widthCm"
                  label="宽度(厘米)"
                >
                  <InputNumber 
                    placeholder="请输入宽度" 
                    className="w-full"
                    min={0}
                    precision={2}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="heightCm"
                  label="高度(厘米)"
                >
                  <InputNumber 
                    placeholder="请输入高度" 
                    className="w-full"
                    min={0}
                    precision={2}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* 提交按钮 */}
          <div className="flex justify-end space-x-4 pt-6">
            <Button 
              size="large" 
              onClick={() => form.resetFields()}
            >
              重置
            </Button>
            <Button 
              type="primary" 
              htmlType="submit" 
              size="large"
              loading={loading}
            >
              创建订单
            </Button>
          </div>
        </Form>
      </Spin>
    </div>
  );
};

const CreateOrderPage: React.FC = () => {
  return (
      <PageBody />
  );
};

export default CreateOrderPage;